import { gsap } from 'gsap';
import { Container, FillGradient, Graphics, Text } from 'pixi.js';
import { motionReduced } from '../ui/motion.js';
import { artSprite } from '../ui/textures.js';
import { COLOR, textStyle } from '../ui/theme.js';
import { keyCap, vertical } from '../ui/widgets.js';
import { drawCapsule } from './jar-view.js';

/** Общие элементы боевых экранов (соло, дуэль, кооператив) по макетам. */

export const caps = (text: string, color: number = COLOR.mint, size = 11): Text =>
  new Text({
    text: text.toUpperCase(),
    style: textStyle({ size, display: true, weight: '800', color, letterSpacing: 1.5 }),
  });

export interface HpColors {
  readonly top: number;
  readonly bottom: number;
}

export const PINK_HP: HpColors = { top: 0xff8fb0, bottom: 0xd94b8c };
export const TEAL_HP: HpColors = { top: 0x8fe3c9, bottom: 0x2fb59a };

/** Здоровье делениями (как на макетах); при большом запасе — сплошной полосой. */
export function hpBar(
  hp: number,
  max: number,
  width: number,
  colors: HpColors = PINK_HP,
  height = 24,
): Graphics {
  const g = new Graphics()
    .roundRect(0, 0, width, height, 8)
    .fill(0x160614)
    .stroke({ width: 2, color: COLOR.ink });
  const inner = width - 8;
  const cell = height - 12;
  if (max <= 20) {
    const gap = 3;
    const w = (inner - gap * (max - 1)) / max;
    for (let i = 0; i < max; i++) {
      g.roundRect(4 + i * (w + gap), 6, w, cell, 3);
      if (i < hp) g.fill(vertical(colors.top, colors.bottom));
      else g.fill({ color: colors.top, alpha: 0.1 });
    }
  } else {
    g.roundRect(4, 6, inner, cell, 3).fill({ color: colors.top, alpha: 0.1 });
    if (hp > 0)
      g.roundRect(4, 6, (inner * hp) / max, cell, 3).fill(vertical(colors.top, colors.bottom));
  }
  return g;
}

/**
 * Кольцо заряда способности со значком в центре. Готовое кольцо пульсирует; остановка — в `stops`.
 * Центр кольца — (0, 0).
 */
export function abilityRing(
  kind: string,
  progress: number,
  ready: boolean,
  radius: number,
  stops: (() => void)[],
  color = 0x7fd8ff,
): Container {
  const ring = new Container();
  const p = Math.max(0, Math.min(1, progress));
  const g = new Graphics().circle(0, 0, radius).fill({ color, alpha: 0.14 });
  if (p > 0) {
    g.moveTo(0, 0)
      .arc(0, 0, radius, -Math.PI / 2, -Math.PI / 2 + p * Math.PI * 2)
      .closePath()
      .fill(color);
  }
  g.circle(0, 0, radius).stroke({ width: Math.max(3, radius / 10), color: COLOR.ink });
  g.circle(0, 0, radius * 0.76).fill(
    new FillGradient({
      type: 'radial',
      center: { x: 0.35, y: 0.3 },
      innerRadius: 0,
      outerCenter: { x: 0.5, y: 0.5 },
      outerRadius: 0.5,
      colorStops: [
        { offset: 0, color: 0x2e6c9a },
        { offset: 1, color: 0x11284a },
      ],
      textureSpace: 'local',
    }),
  );
  ring.addChild(g, artSprite(`icon-ability-${kind}`, radius * 0.8));
  if (ready) {
    const glow = new Graphics().circle(0, 0, radius + 4).stroke({ width: 4, color, alpha: 0.6 });
    ring.addChildAt(glow, 0);
    if (!motionReduced()) {
      const tween = gsap.to(glow, {
        alpha: 0.15,
        duration: 0.9,
        yoyo: true,
        repeat: -1,
        ease: 'sine.inOut',
      });
      stops.push(() => {
        tween.kill();
      });
    }
  }
  return ring;
}

/** Подсказки управления внизу экрана: клавиши и действие. Ширина — в `width` результата. */
export function keyFooter(
  rows: readonly (readonly [readonly string[], string])[],
  lead = '',
): Container {
  const bar = new Container();
  let x = 0;
  if (lead) {
    const t = caps(lead, COLOR.mint, 12);
    t.anchor.set(0, 0.5);
    t.position.set(0, 15);
    bar.addChild(t);
    x += t.width + 24;
  }
  for (const [keys, action] of rows) {
    for (const key of keys) {
      const cap = keyCap(key);
      cap.x = x;
      bar.addChild(cap);
      x += cap.width + 8;
    }
    const t = new Text({ text: action, style: textStyle({ size: 14, color: COLOR.textSoft }) });
    t.anchor.set(0, 0.5);
    t.position.set(x, 15);
    bar.addChild(t);
    x += t.width + 26;
  }
  return bar;
}

/** Капсулы рядом: «Следующая», варианты Стратега. */
export function capsuleRow(
  pieces: readonly (readonly number[])[],
  s: number,
  frost = false,
  candy = false,
  gap = 10,
): Graphics {
  const g = new Graphics();
  pieces.forEach((colors, i) => {
    colors
      .slice(0, 2)
      .forEach((color, half) =>
        drawCapsule(
          g,
          color,
          half === 0 ? 'L' : 'R',
          i * (2 * s + gap) + half * s,
          0,
          s,
          false,
          frost,
          true,
          candy,
        ),
      );
  });
  return g;
}
